import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Star, Wrench, ShieldCheck, Zap, Quote } from "lucide-react";
import Navbar from "../components/lac/Navbar";
import Footer from "../components/lac/Footer";
import TikTokFeed from "../components/lac/TikTokFeed";
import BrandsStrip from "../components/lac/BrandsStrip";
import OurStory from "../components/lac/OurStory";
import WhatsAppButton from "../components/lac/WhatsAppButton";
import RegLookup from "../components/lac/RegLookup";
import ComboPopup from "../components/lac/ComboPopup";
import { LOGO_URL } from "../components/lac/LacBadge";

const HERO_VIDEO = "/video/lac-hero";
const HERO_POSTER = "/video/lac-hero-poster.webp";
const STARLIGHT_IMG = "https://customer-assets.emergentagent.com/job_lac-customs-booking/artifacts/izqcl26x_IMG_0427.jpeg";
const AMBIENT_IMG = "https://customer-assets.emergentagent.com/job_lac-customs-booking/artifacts/zj8tp4v7_IMG_0409.jpeg";
const PARTS_IMG = "https://images.unsplash.com/photo-1716914175680-363268735181?fm=jpg&q=85&w=1600&auto=format&fit=crop";

const services = [
  {
    icon: Star,
    title: "Starlights",
    img: STARLIGHT_IMG,
    desc: "Hand-fitted fibre-optic headliners with shooting stars, twinkle effects & full constellation patterns. Transform your roof into a private sky.",
    price: "from £400",
    priceNote: "500 → 1000 star packages",
    accent: "#D946EF",
    features: ["Hand-laid fibre optics", "Shooting star & twinkle modes", "Roof & sun-visor integration", "Custom constellation layouts"],
  },
  {
    icon: Sparkles,
    title: "Ambient Lights",
    img: AMBIENT_IMG,
    desc: "Multi-zone RGB ambient lighting integrated through doors, dash, footwells & seats. App-controlled. 64-colour palettes. Music-reactive.",
    price: "from £300",
    priceNote: "Enquire for full packages",
    accent: "#FF2D87",
    features: ["Factory-coded installation", "64-colour multi-zone RGB", "No loose wires · OEM finish", "Music-reactive option"],
  },
  {
    icon: Wrench,
    title: "Custom Parts",
    img: PARTS_IMG,
    desc: "BMW digital cockpit retrofits, bespoke trim, illuminated badges, and custom fabrications — every build is one-of-one, so get in touch for a free consultation and we'll quote it properly.",
    price: "Contact for consultation",
    priceNote: "Tailored per build",
    accent: "#D946EF",
    features: ["Digital cockpit retrofits", "Illuminated badges & trim", "Bespoke fabrication", "One-of-one builds"],
  },
];

const features = [
  { icon: ShieldCheck, title: "OEM-Grade Quality", text: "Premium fibres, automotive-rated wiring, and finishes that survive every season." },
  { icon: Zap, title: "Plug & Play Install", text: "No cutting, no compromise. Your warranty stays intact, your interior stays factory-tight." },
  { icon: Sparkles, title: "Designed in London", text: "Hand-built by enthusiasts with hundreds of installs documented on TikTok @lac.ltd" },
];

const testimonials = [
  {
    name: "Jack Bosher",
    car: "Google Review · a year ago",
    quote: "I have recently had the full ambient lighting kit installed on my m135i and couldn't be happier with it. From the communication telling me how it all works & the different options I could've opted for to the customer service, everything was perfect. I'd highly recommend LAC Ltd to anyone I know and believe the quality of the work speaks for itself. Thanks again!",
  },
  {
    name: "Antonio Teti",
    car: "Google Review · a year ago",
    quote: "Had LED lights fitted on my car and they did an amazing job! The lights are perfectly installed and add just the right touch to my car's look. Very professional, cheap, quick, and clearly knew what they were doing. They have also found a blown fuse for my cigarette lighter, and replaced it with no extra charge. Highly recommend for anyone looking to add a special touch to their vehicle!",
  },
  {
    name: "Jamilah Aktar",
    car: "Google Review · a year ago",
    quote: "LAC completely transformed my car! Looks really professional and they provide amazing service. Very helpful and friendly, will definitely recommend them and will come back for other services.",
  },
  {
    name: "Anja Hasanaj",
    car: "Google Review · 8 months ago",
    quote: "I couldn't be happier with the ambient lighting installed by LAC LTD! It looks incredible and adds such a great look to the car's interior. Everything is of great quality and great price; the team was very helpful, quick, and provided excellent customer service. Highly recommend for anyone looking to upgrade their interior!!",
  },
  {
    name: "Jimmy Finnan",
    car: "Google Review · 8 months ago",
    quote: "Amazing job by LAC LTD. All round great experience — he made sure to run me through everything beforehand and did exactly as he said he would. Very friendly and talented, would highly recommend.",
  },
];

export default function Landing() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0">
          <div className="absolute inset-0 lg:left-auto lg:w-[42%] 2xl:w-[36%]">
            <video
              poster={HERO_POSTER}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-label="LAC ambient lighting install"
              data-testid="hero-video"
              className="w-full h-full object-cover object-center opacity-55 lg:opacity-90"
            >
              <source src={`${HERO_VIDEO}.mp4`} type="video/mp4" />
              <source src={`${HERO_VIDEO}.webm`} type="video/webm" />
            </video>
            <div className="absolute inset-0 lg:hidden bg-gradient-to-t from-[#050505] via-[#050505]/45 to-[#050505]/25" />
            <div className="absolute inset-0 hidden lg:block bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            <div className="absolute inset-0 hidden lg:block bg-gradient-to-r from-[#050505] via-[#050505]/10 to-transparent" />
            <img
              src={LOGO_URL}
              alt=""
              data-testid="hero-logo-watermark"
              className="absolute top-24 right-4 lg:top-28 lg:right-8 w-12 h-12 lg:w-16 lg:h-16 rounded-full opacity-70 select-none pointer-events-none"
            />
          </div>
          <div className="starfield" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-8 items-center w-full">
          <div className="lg:col-span-8 float-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D946EF]/30 bg-[#D946EF]/5 text-[11px] tracking-[0.25em] text-[#D946EF] mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D946EF] animate-pulse" />
              NOW BOOKING · RAINHAM
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Get your car done right the first time,<br />
              <span className="neon-cyan">the LAC way.</span>
            </h1>
            <p className="mt-8 text-zinc-300 text-lg max-w-xl font-body leading-relaxed">
              Ambient lighting specialists · Rainham, East London. Bespoke starlight headliners,
              multi-zone ambient lighting, and custom parts — fitted by enthusiasts, finished like factory.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/booking"
                data-testid="hero-book-cta"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#D946EF] text-white font-semibold pulse-glow hover:bg-[#F0ABFC] transition"
              >
                Book your install
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </Link>
              <a
                href="#services"
                data-testid="hero-services-cta"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/15 text-white hover:border-[#D946EF]/60 hover:text-[#D946EF] transition"
              >
                Explore services
              </a>
            </div>

            <div className="mt-16 flex items-center gap-10 text-sm text-zinc-400">
              <div><div className="text-2xl font-display font-bold text-white">1000+</div>installs</div>
              <div className="w-px h-10 bg-white/10" />
              <div><div className="text-2xl font-display font-bold text-white">4.9★</div>customer rating</div>
              <div className="w-px h-10 bg-white/10" />
              <div><div className="text-2xl font-display font-bold text-white">2–3h</div>turnaround</div>
            </div>
          </div>
        </div>
      </section>

      {/* REG LOOKUP */}
      <RegLookup />

      {/* SERVICES */}
      <section id="services" className="py-32 relative" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
            <div>
              <div className="text-xs tracking-[0.35em] text-[#D946EF] mb-4">/ 01 — SERVICES</div>
              <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-2xl">
                Three signatures.<br />Endless combinations.
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md font-body">
              Every build is briefed, designed, and installed in-house. Mix any service —
              we'll quote it as one cohesive transformation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`service-card-${i}`}
                  className="group relative overflow-hidden rounded-sm border border-white/5 bg-[#0c0c0c] hover:-translate-y-1 transition-all duration-500 glow-border-hover flex flex-col"
                >
                  <div className="relative aspect-[5/3] overflow-hidden">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-transparent to-transparent" />
                    <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 text-xs font-semibold tracking-wide" style={{ color: s.accent }}>
                      {s.price}
                    </div>
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <Icon className="w-6 h-6 mb-3" style={{ color: s.accent, filter: `drop-shadow(0 0 12px ${s.accent})` }} />
                    <h3 className="font-display font-bold text-2xl tracking-tight">{s.title}</h3>
                    <div className="text-[11px] tracking-[0.2em] text-zinc-500 mt-1 mb-4">{s.priceNote}</div>
                    <p className="text-zinc-400 text-sm font-body leading-relaxed">{s.desc}</p>

                    <ul className="mt-5 space-y-2 mb-6">
                      {s.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                          <span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: s.accent }} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/booking"
                      data-testid={`service-book-${i}`}
                      className="mt-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white transition hover:opacity-90"
                      style={{ background: s.accent }}
                    >
                      Book {s.title} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* COMBO BANNER → moved to ComboPopup, which fires when this section scrolls into view */}
        </div>
      </section>

      {/* BROWSE BY CAR */}
      <BrandsStrip />

      {/* WHY US / FEATURES */}
      <section id="about" className="py-32 relative" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.35em] text-[#FF2D87] mb-4">/ 02 — WHY LAC</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tighter">
              Built by people<br />who actually <span className="neon-violet">care</span>.
            </h2>
            <p className="text-zinc-400 mt-6 font-body leading-relaxed">
              We work with every car like it's our own — fibre by fibre, strip by strip.
              Years of installs across Teslas, Range Rovers, BMWs, Mercs and beyond means
              your car gets the experience, not a test run. Every customer walks away with
              the perfect set of lights.
            </p>
            <div className="mt-10 flex gap-4">
              <Link
                to="/booking"
                data-testid="about-book-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D946EF] text-white font-semibold hover:bg-[#F0ABFC] transition"
              >
                Start your build <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="p-7 border border-white/5 bg-[#0c0c0c]/60 rounded-sm hover:border-[#D946EF]/30 transition group">
                  <Icon className="w-6 h-6 text-[#D946EF] mb-5 group-hover:scale-110 transition" />
                  <h3 className="font-display font-semibold text-xl mb-2">{f.title}</h3>
                  <p className="text-zinc-400 text-sm font-body leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <OurStory />

      {/* TIKTOK FEED → Recent work */}
      <TikTokFeed />

      {/* TESTIMONIALS */}
      <section className="py-24 bg-[#080808] border-y border-white/5" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="text-xs tracking-[0.35em] text-[#FF2D87] mb-4">/ 04 — GOOGLE REVIEWS</div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter">Real customers. Five stars.</h2>
            </div>
            <a
              data-testid="google-reviews-cta"
              href="https://share.google/LlicaWLghMbT2urbY"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#D946EF] transition"
            >
              See all on Google <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div key={i} data-testid={`testimonial-card-${i}`} className="p-8 border border-white/5 bg-[#050505]/80 rounded-sm relative">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-1">
                    {[0,1,2,3,4].map((n) => (
                      <Star key={n} className="w-4 h-4 text-yellow-400" fill="currentColor" strokeWidth={0} />
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] text-zinc-500">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917"/><path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.9 11.9 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917"/></svg>
                    GOOGLE
                  </div>
                </div>
                <Quote className="w-5 h-5 text-[#D946EF]/60 mb-3" />
                <p className="text-zinc-200 font-body leading-relaxed">"{t.quote}"</p>
                <div className="mt-6 pt-5 border-t border-white/5">
                  <div className="font-display font-semibold">{t.name}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{t.car}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT / CTA */}
      <section id="contact" className="py-32" data-testid="contact-section">
        <div className="max-w-5xl mx-auto px-6 text-center relative">
          <div className="absolute inset-0 starfield opacity-30" />
          <div className="relative">
            <div className="text-xs tracking-[0.35em] text-[#D946EF] mb-6">/ 05 — READY?</div>
            <h2 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter">
              Your car. <span className="neon-cyan">Your sky.</span><br />Pick a date.
            </h2>
            <p className="text-zinc-400 mt-8 max-w-xl mx-auto font-body">
              Booking takes under a minute. We'll confirm your slot by email and prep the install
              before you arrive. Now open at our new HQ — Unit 15, Rainham, RM13 8HZ.
            </p>
            <div className="mt-6 flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-sm text-zinc-300">
              <a href="tel:+447538963090" className="inline-flex items-center gap-2 hover:text-[#FF2D87] transition" data-testid="contact-phone">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.4 11.4 0 0 0 .57 3.6 1 1 0 0 1-.25 1Z"/></svg>
                07538 963090
              </a>
              <span className="inline-flex items-center gap-2">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12 2a8 8 0 0 0-8 8c0 5.5 8 12 8 12s8-6.5 8-12a8 8 0 0 0-8-8Zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6Z"/></svg>
                Unit 15, Rainham, RM13 8HZ<br/>
                <span className="text-xs text-[#D946EF]">Grand opening — use code LACHQ10 for 10% off</span>
              </span>
            </div>
            <div className="mt-10 flex flex-wrap justify-center items-center gap-4">
              <Link
                to="/booking"
                data-testid="contact-book-cta"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D946EF] text-white font-semibold pulse-glow hover:bg-[#F0ABFC] transition"
              >
                Book my appointment <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://www.tiktok.com/@lac.ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/15 text-white hover:border-[#FF2D87]/60 hover:text-[#FF6BB0] transition"
              >
                Watch on TikTok @lac.ltd
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <ComboPopup targetSelector="#services" />
    </div>
  );
}
